import { PayerId, PaymentOptionId } from "~/common/constants";
import {
  isAdditionalAlignersCourse,
  isCapLikeCourse,
  isChildrenCourse,
  isPowerCapCourse,
  isRetainersCourse,
  isTeenCourse,
  Status,
} from "~/common/courses";
import { convertReduxFormOcclusalPlaneTeethToArray } from "~/common/instructions";
import {
  Arch,
  getOcclusalPlaneTeeth,
  IncisorsVerticalOverlap,
  Material,
  OcclusalPlane,
  OcclusalPlaneAction,
} from "~/common/prescription";
import { canOrderTestPlastic } from "~/common/user";
import { getEnumNumericValues } from "~/common/utils";
import type { TInstructions } from "~/slices/instructions";
import type { RootState } from "~/store";
import type { TPatient } from "~/types/patient";
import type { TPrescriptionReduxForm } from "~/types/redux-form";
import type { TUser } from "~/types/user";

import type { TAlignersFormValues } from "./patient_new/patient_new_additional_aligners";
import { isSmartPrescriptionFillingSelected } from "./patient_new/patient_new_additional_services";

/**
 * When you update a patient, depending on the status, form values could be `undefined`.
 */
export type PatientUpdateFormValues = TPrescriptionReduxForm | undefined;

export const PATIENT_NEW_REQUIRED_FIELDS = [
  "validation-clinic_id",
  "validation-payer_id",
  "validation-payment_method_id",
  "validation-payment_option_id",
  "validation-patient",
  "patient-block-body",
  "validation-email",
  "validation-course_id",
  "validation-condition",
  "validation-comment",
  "validation-material",
  "validation-test_plastic",
  "validation-arch",
  "validation-vertical_overlap_comment",
  "validation-midline",
  "validation-occlusal_plane",
  "validation-quantity",
  "validation-files",
  "photo-protocol-block",
] as const;

export type TPatientNewRequiredField = (typeof PATIENT_NEW_REQUIRED_FIELDS)[number];

export function validateReqiredFields({
  user,
  patient,
  links,
  instructions,
  formValues,
  additionalAlignersFormValues,
  media,
  emptyFields,
}: {
  user: Partial<TUser>;
  patient?: TPatient;
  links: string[];
  instructions: Partial<TInstructions>;
  formValues: PatientUpdateFormValues;
  additionalAlignersFormValues: TAlignersFormValues | undefined;
  media: RootState["media"];
  emptyFields: TPatientNewRequiredField[];
}): boolean {
  const hasSmartPrescriptionFilling = isSmartPrescriptionFillingSelected(
    instructions.additional_service_tags,
  );
  const isPatientNameValid = validatePatientName(instructions, emptyFields);
  const isMaterialValid = validateMaterial(
    instructions,
    formValues,
    emptyFields,
    hasSmartPrescriptionFilling,
  );
  const isVerticalOverlapCommentValid = hasSmartPrescriptionFilling
    ? true
    : validateVerticalOverlapComment(formValues, emptyFields);
  const isTestPlasticValid = canOrderTestPlastic(user)
    ? validateTestPlastic(instructions, emptyFields)
    : true;
  const isEmailValid = validateEmail(instructions, emptyFields);
  const isPayerValid = validatePayer(instructions, emptyFields);
  const isPaymentOptionValid = validatePaymentOption(instructions, emptyFields);
  const areClinicAndPaymentMethodAndCourseValid = validateClinicAndPaymentMethodAndCourse(
    instructions,
    emptyFields,
  );
  const areLinksValid = hasSmartPrescriptionFilling ? true : validateLinks(links, emptyFields);
  const isArchValid = validateArch(instructions, formValues, emptyFields);
  const isPatientPayerNameValidForChildrenOrTeenCourse =
    validatePatientPayerNameForChildrenOrTeenCourse(instructions, emptyFields);
  const isOcclusalPlaneValid = hasSmartPrescriptionFilling
    ? true
    : validateOcclusalPlane(instructions, formValues, emptyFields);
  const arePhotosValid = hasSmartPrescriptionFilling
    ? true
    : validatePhotos(instructions, media, emptyFields, patient);
  const isBirthDateValid = !instructions.bdate || instructions.bdate !== "Invalid date";
  const isQuantityValid = validateQuantity(instructions, formValues, emptyFields);
  const areFilesValid = hasSmartPrescriptionFilling
    ? true
    : validateFiles(instructions, formValues, media, emptyFields, patient);
  const isPatientValid = validatePatient(instructions, emptyFields);
  const areAdditionalAlignersValid = validateAdditionalAlignersFields(
    instructions,
    additionalAlignersFormValues,
    emptyFields,
  );

  const areRequiredFieldsValid =
    isPatientNameValid &&
    isMaterialValid &&
    isVerticalOverlapCommentValid &&
    isTestPlasticValid &&
    isEmailValid &&
    isPayerValid &&
    isPaymentOptionValid &&
    areClinicAndPaymentMethodAndCourseValid &&
    areLinksValid &&
    isArchValid &&
    isPatientPayerNameValidForChildrenOrTeenCourse &&
    isOcclusalPlaneValid &&
    arePhotosValid &&
    isBirthDateValid &&
    isQuantityValid &&
    areFilesValid &&
    isPatientValid &&
    areAdditionalAlignersValid;

  return areRequiredFieldsValid;
}

export function validateRequiredDraftFields({
  links,
  instructions,
  formValues,
  emptyFields,
}: {
  links: string[];
  instructions: Partial<TInstructions>;
  formValues: PatientUpdateFormValues;
  emptyFields: TPatientNewRequiredField[];
}): boolean {
  const hasSmartPrescriptionFilling = isSmartPrescriptionFillingSelected(
    instructions.additional_service_tags,
  );
  const isPatientNameValid = validatePatientName(instructions, emptyFields);
  const isVerticalOverlapCommentValid = hasSmartPrescriptionFilling
    ? true
    : validateVerticalOverlapComment(formValues, emptyFields);
  const areLinksValid = hasSmartPrescriptionFilling ? true : validateLinks(links, emptyFields);
  // NOTE: If `MODIFY` is selected as `occlusal_plane_action`, then the `occlusal_plane_subaction`
  // field must be filled for backend to accept the data.
  const isOcclusalPlaneValid =
    !hasSmartPrescriptionFilling && formValues?.occlusal_plane_action == OcclusalPlaneAction.MODIFY
      ? validateOcclusalPlane(instructions, formValues, emptyFields)
      : true;

  return (
    isPatientNameValid && isVerticalOverlapCommentValid && areLinksValid && isOcclusalPlaneValid
  );
}

export function validatePatientName(
  instructions: Partial<TInstructions>,
  emptyFields: TPatientNewRequiredField[],
): boolean {
  if (isAdditionalAlignersCourse(instructions.course_id)) {
    return true;
  }

  return (
    (["first_name", "last_name"] as const).filter((field) => {
      let value = instructions[field];
      if (typeof value == "string") {
        value = value.trim();
      }
      const isValid = Boolean(value);

      $(`[name="${field}"]`)
        .parent()
        .find("label")
        .css({ color: !isValid ? "red" : "#34495e" });

      if (!isValid) {
        emptyFields.push("patient-block-body");
      }

      return !isValid;
    }).length == 0
  );
}

function validateMaterial(
  instructions: Partial<TInstructions>,
  formValues: PatientUpdateFormValues,
  emptyFields: TPatientNewRequiredField[],
  hasSmartPrescriptionFilling = false,
): boolean {
  // Power-cap material is inferred from uploads rather than selected in the form.
  if (
    isAdditionalAlignersCourse(instructions.course_id) ||
    hasSmartPrescriptionFilling ||
    isPowerCapCourse(instructions.course_id)
  ) {
    return true;
  }

  const { material } = formValues ?? {};
  const isValid = material == Material.SCAN || material == Material.IMPRESSIONS;

  if (!isValid) {
    emptyFields.push("validation-material");
  }
  highlight("#validation-material", isValid);

  return isValid;
}

export function validateVerticalOverlapComment(
  formValues: PatientUpdateFormValues,
  emptyFields: TPatientNewRequiredField[],
): boolean {
  const { vertical_overlap, vertical_overlap_comment = "" } = formValues ?? {};

  const shouldValidate =
    vertical_overlap == IncisorsVerticalOverlap.DECREASE ||
    vertical_overlap == IncisorsVerticalOverlap.INCREASE;

  if (!shouldValidate) {
    return true;
  }

  const isValid = vertical_overlap_comment.trim().length > 0;

  if (!isValid) {
    emptyFields.push("validation-vertical_overlap_comment");
  }
  highlight(`#validation-vertical_overlap_comment`, isValid);

  return isValid;
}

export function validatePatient(
  instructions: Partial<TInstructions>,
  emptyFields: TPatientNewRequiredField[],
): boolean {
  if (!isAdditionalAlignersCourse(instructions.course_id)) {
    return true;
  }

  const isValid = typeof instructions.patient_id == "number";

  if (!isValid) {
    emptyFields.push("validation-patient");
  }
  highlight("#validation-patient", isValid);

  return isValid;
}

function validateTestPlastic(
  { test_plastic }: Partial<TInstructions>,
  emptyFields: TPatientNewRequiredField[],
): boolean {
  const isValid = typeof test_plastic == "boolean";

  if (!isValid) {
    emptyFields.push("validation-test_plastic");
  }
  highlight("#validation-test_plastic", isValid);

  return isValid;
}

function validatePayer(
  instructions: Partial<TInstructions>,
  emptyFields: TPatientNewRequiredField[],
): boolean {
  const isValid =
    instructions.payer_id != null && getEnumNumericValues(PayerId).includes(instructions.payer_id);

  if (!isValid) {
    emptyFields.push("validation-payer_id");
  }
  highlight("#validation-payer_id", isValid);

  return isValid;
}

function validatePaymentOption(
  instructions: Partial<TInstructions>,
  emptyFields: TPatientNewRequiredField[],
): boolean {
  const isValid =
    (instructions.payment_option_id != null &&
      getEnumNumericValues(PaymentOptionId).includes(instructions.payment_option_id)) ||
    instructions.sber_credit == true;

  if (!isValid) {
    emptyFields.push("validation-payment_option_id");
  }
  highlight("#validation-payment_option_id", isValid);

  return isValid;
}

function validateEmail(
  instructions: Partial<TInstructions>,
  emptyFields: TPatientNewRequiredField[],
): boolean {
  if (instructions.payer_id != PayerId.PATIENT) {
    return true;
  }

  const isValid = typeof instructions.email == "string" && isValidEmailAddress(instructions.email);

  if (!isValid) {
    emptyFields.push("validation-email");
  }
  highlight("#validation-email", isValid);

  return isValid;
}

function validateClinicAndPaymentMethodAndCourse(
  instructions: Partial<TInstructions>,
  emptyFields: TPatientNewRequiredField[],
): boolean {
  return !(["clinic_id", "payment_method_id", "course_id"] as const).filter((field) => {
    const isValid = instructions[field] != null && !Number.isNaN(instructions[field]);

    if (!isValid) {
      emptyFields.push(`validation-${field}`);
    }
    highlight(`#validation-${field}`, isValid);

    return !isValid;
  }).length;
}

function validateArch(
  instructions: Partial<TInstructions>,
  formValues: PatientUpdateFormValues,
  emptyFields: TPatientNewRequiredField[],
): boolean {
  if (
    isAdditionalAlignersCourse(instructions.course_id) ||
    isSmartPrescriptionFillingSelected(instructions.additional_service_tags)
  ) {
    return true;
  }

  const { arch } = formValues ?? {};
  const isValid = Object.values(Arch).includes(arch);

  if (!isValid) {
    emptyFields.push("validation-arch");
  }
  highlight("#validation-arch", isValid);

  return isValid;
}

export function validateLinks(links: string[], emptyFields: TPatientNewRequiredField[]): boolean {
  return (
    links.filter((link) => {
      const isValid = link != "";

      if (!isValid) {
        emptyFields.push("validation-link");
      }

      return !isValid;
    }).length == 0
  );
}

export const PATIENT_NEW_REQUIRED_PHOTOS = [
  "front_view",
  "full_face_with_smile",
  "full_face_without_smile",
  "profile",
  "lateral_view_left",
  "lateral_view_right",
  "occlusal_view_lower",
  "occlusal_view_upper",
] as const;

function validatePhotos(
  instructions: Partial<TInstructions>,
  media: RootState["media"],
  emptyFields: TPatientNewRequiredField[],
  patient?: TPatient,
): boolean {
  // On patient update the photo upload block is available only for draft cases.
  // Do not block save by hidden fields when case is already submitted.
  if (patient && patient.status != Status.UNFILLED_CASE) {
    return true;
  }

  return !PATIENT_NEW_REQUIRED_PHOTOS.filter((elm) => {
    if (
      isCapLikeCourse(instructions.course_id) ||
      isAdditionalAlignersCourse(instructions.course_id)
    ) {
      return false;
    }

    let isValid = media[elm] != null && !media[elm]?.removed && media[elm]?.md5 != null;

    if (!isValid && patient && !media[elm]?.removed) {
      isValid = patient.media.required_images[elm] != null;
    }

    if (!isValid) {
      emptyFields.push("photo-protocol-block");
    }

    $(`#${elm}`)
      .parent()
      .prev()
      .css({ color: !isValid ? "red" : "#34495e" });

    return !isValid;
  }).length;
}

function validatePatientPayerNameForChildrenOrTeenCourse(
  instructions: Partial<TInstructions>,
  emptyFields: TPatientNewRequiredField[],
): boolean {
  const { payer_id, course_id } = instructions;

  const shouldValidate =
    payer_id == PayerId.PATIENT && (isChildrenCourse(course_id) || isTeenCourse(course_id));

  if (!shouldValidate) {
    return true;
  }

  return (
    (["payer_first_name", "payer_last_name", "payer_patronymic"] as const).filter((field) => {
      const isValid = Boolean(instructions[field]);

      if (!isValid) {
        emptyFields.push("patient-block-body");
      }

      $(`[name="${field}"]`)
        .parent()
        .find("label")
        .css({ color: !isValid ? "red" : "#34495e" });

      return !isValid;
    }).length == 0
  );
}

function validateOcclusalPlane(
  instructions: Partial<TInstructions>,
  formValues: PatientUpdateFormValues,
  emptyFields: TPatientNewRequiredField[],
) {
  if (
    isCapLikeCourse(instructions.course_id) ||
    isAdditionalAlignersCourse(instructions.course_id) ||
    isSmartPrescriptionFillingSelected(instructions.additional_service_tags)
  ) {
    return true;
  }

  const isValid = isOcclusalPlaneValid(formValues);

  if (!isValid) {
    emptyFields.push("validation-occlusal_plane");
  }
  highlight("#validation-occlusal_plane", isValid);

  return isValid;
}

function validateQuantity(
  instructions: Partial<TInstructions>,
  formValues: PatientUpdateFormValues,
  emptyFields: TPatientNewRequiredField[],
) {
  if (!isRetainersCourse(instructions.course_id)) {
    return true;
  }

  const isValid = formValues?.quantity == 1 || formValues?.quantity == 2;

  if (!isValid) {
    emptyFields.push("validation-quantity");
  }
  highlight("#validation-quantity", isValid);

  return isValid;
}

function validateFiles(
  instructions: Partial<TInstructions>,
  formValues: PatientUpdateFormValues,
  media: RootState["media"],
  emptyFields: TPatientNewRequiredField[],
  patient?: TPatient,
) {
  // Power-cap orders use one required uploader for STL, photos, OPTG and other files.
  if (isPowerCapCourse(instructions.course_id)) {
    const hasUploadedFiles = Object.keys(media).length > 0;
    const hasExistingFiles = Boolean(patient && patient.media.optional_media.length > 0);
    const isValid = hasUploadedFiles || hasExistingFiles;

    if (!isValid) {
      emptyFields.push("validation-files");
    }
    highlight("#validation-files", isValid);

    return isValid;
  }

  if (!isCapLikeCourse(instructions.course_id)) {
    return true;
  }

  let isScanUploaded = Object.entries(media).some(([_, { user_filename }]) =>
    user_filename?.endsWith(".stl"),
  );

  if (patient && !isScanUploaded) {
    const isScanAlreadyUploaded = patient.media.optional_media.length > 0;
    isScanUploaded = isScanAlreadyUploaded;
  }

  const isValid = formValues?.material != Material.SCAN || isScanUploaded;

  if (!isValid) {
    emptyFields.push("validation-files");
  }
  highlight("#validation-files", isValid);

  return isValid;
}

export function isOcclusalPlaneValid(formValues: PatientUpdateFormValues): boolean {
  const { occlusal_plane, occlusal_plane_action, occlusal_plane_subaction } = formValues ?? {};

  let isValid = occlusal_plane != null;

  if (isValid && (occlusal_plane == OcclusalPlane.RIGHT || occlusal_plane == OcclusalPlane.LEFT)) {
    isValid =
      occlusal_plane_action == OcclusalPlaneAction.SAVE ||
      occlusal_plane_action == OcclusalPlaneAction.MODIFY;

    if (isValid && occlusal_plane_action == OcclusalPlaneAction.MODIFY) {
      isValid =
        occlusal_plane_subaction == OcclusalPlaneAction.EXTRUDE ||
        occlusal_plane_subaction == OcclusalPlaneAction.INTRUDE;

      if (isValid) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const occlusalPlaneTeeth = convertReduxFormOcclusalPlaneTeethToArray(formValues!);

        isValid =
          occlusalPlaneTeeth.length > 0 &&
          occlusalPlaneTeeth.length <=
            getOcclusalPlaneTeeth(occlusal_plane, occlusal_plane_subaction).length;
      }
    }
  }

  return isValid;
}

export function validateAdditionalAlignersFields(
  instructions: Partial<TInstructions>,
  additionalAlignersFormValues: TAlignersFormValues | undefined,
  emptyFields: TPatientNewRequiredField[],
): boolean {
  if (!isAdditionalAlignersCourse(instructions.course_id)) {
    return true;
  }

  const aligners = additionalAlignersFormValues?.aligners ?? [];
  let isValid = true;

  for (const [index, aligner] of aligners.entries()) {
    const isStageValid = aligner.stage != null;
    if (!isStageValid) {
      emptyFields.push(`validation-aligners-${index}-stage` as TPatientNewRequiredField);
    }
    highlight(`#validation-aligners-${index}-stage`, isStageValid);

    const isArchValid = aligner.treat_arch_id != null;
    if (!isArchValid) {
      emptyFields.push(`validation-aligners-${index}-treat_arch_id` as TPatientNewRequiredField);
    }
    highlight(`#validation-aligners-${index}-treat_arch_id`, isArchValid);

    const isAmountValid = Boolean(aligner.amount);
    if (!isAmountValid) {
      emptyFields.push(`validation-aligners-${index}-amount` as TPatientNewRequiredField);
    }
    highlight(`#validation-aligners-${index}-amount`, isAmountValid);

    const isAlignerValid = isStageValid && isArchValid && isAmountValid;
    if (!isAlignerValid) {
      isValid = false;
    }
  }

  return isValid;
}

function isValidEmailAddress(value: string): boolean {
  const EMAIL_REGEX =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return EMAIL_REGEX.test(value.trim());
}

function highlight(selector: string, isValid: boolean): void {
  $(selector).css({ color: !isValid ? "red" : "#34495e" });
}
